/**
 * TelemetryFeatures
 *
 * Lightweight, allocation-free (after init) feature extraction utilities.
 * Ported from Swift to JS.
 */

/**
 * Ring buffer for O(1) sliding window statistics (mean/std).
 */
export class RollingStats {
  /**
   * @param {number} windowSize
   */
  constructor(windowSize) {
    if (!Number.isFinite(windowSize) || windowSize <= 0) {
      throw new Error(`RollingStats: windowSize must be a positive number (got ${windowSize})`);
    }

    /** @type {number} */
    this.windowSize = windowSize;

    /** @type {number[]} */
    this._buffer = new Array(windowSize).fill(0);
    /** @type {number} */
    this._index = 0;
    /** @type {number} */
    this._count = 0;
    /** @type {number} */
    this._sum = 0;
    /** @type {number} */
    this._sumSquares = 0;
  }

  /**
   * Push a new value into the rolling window.
   * Returns null until the window is full.
   *
   * @param {number} value
   * @returns {{ mean: number, std: number } | null}
   */
  push(value) {
    if (!Number.isFinite(value)) return null;

    // Remove oldest value from sums if buffer is full
    if (this._count === this.windowSize) {
      const oldest = this._buffer[this._index];
      this._sum -= oldest;
      this._sumSquares -= oldest * oldest;
    } else {
      this._count += 1;
    }

    // Add new value
    this._buffer[this._index] = value;
    this._sum += value;
    this._sumSquares += value * value;
    this._index = (this._index + 1) % this.windowSize;

    if (this._count !== this.windowSize) return null;

    const mean = this._sum / this.windowSize;
    const variance = this._sumSquares / this.windowSize - mean * mean;
    const std = Math.sqrt(Math.max(0, variance));

    return { mean, std };
  }
}

/**
 * Heading delta with wraparound handling.
 * Expects degrees where 0..360 wraps at 360.
 *
 * @param {number} from
 * @param {number} to
 * @returns {number}
 */
export function headingDelta(from, to) {
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;

  let delta = to - from;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

/**
 * Complete feature extractor - runs per GPS sample.
 */
export class SkiLiftFeatureExtractor {
  constructor() {
    /** @type {number | null} */
    this._lastAltitude = null;
    /** @type {number | null} */
    this._lastSpeed = null;
    /** @type {number | null} */
    this._lastCourse = null;
    /** @type {number | null} */
    this._lastTimestamp = null;

    // Rolling stats with ~10 second window at 1Hz GPS
    this._verticalVelocityStats = new RollingStats(10);
    this._speedStats = new RollingStats(10);
    this._turnRateStats = new RollingStats(10);
    this._accelerationStats = new RollingStats(10);
  }

  /**
   * Process one telemetry sample.
   * Returns null until we have a previous sample AND the rolling windows are full.
   *
   * @param {number} timestamp - seconds (or any monotonic timebase), must increase
   * @param {number} altitude - meters
   * @param {number} speed - m/s
   * @param {number} course - degrees
   * @returns {null | {
   *   verticalVelocity: number,
   *   speed: number,
   *   acceleration: number,
   *   turnRate: number,
   *   verticalVelocityMean: number,
   *   verticalVelocityStd: number,
   *   speedMean: number,
   *   speedStd: number,
   *   turnRateMean: number,
   *   turnRateStd: number,
   *   isDescending: boolean,
   *   hasConstantSpeed: boolean,
   *   isStraightLine: boolean,
   * }}
   */
  process(timestamp, altitude, speed, course) {
    const prevAlt = this._lastAltitude;
    const prevSpeed = this._lastSpeed;
    const prevCourse = this._lastCourse;
    const prevTime = this._lastTimestamp;

    // Mirror Swift `defer { ... }`
    this._lastAltitude = altitude;
    this._lastSpeed = speed;
    this._lastCourse = course;
    this._lastTimestamp = timestamp;

    if (
      prevAlt === null ||
      prevSpeed === null ||
      prevCourse === null ||
      prevTime === null
    ) {
      return null;
    }

    const dt = timestamp - prevTime;
    if (!Number.isFinite(dt) || dt <= 0) return null;

    // Instantaneous derivatives
    const verticalVelocity = (altitude - prevAlt) / dt;
    const acceleration = (speed - prevSpeed) / dt;
    const turnRate = headingDelta(prevCourse, course) / dt;

    // Update rolling stats
    const vvStats = this._verticalVelocityStats.push(verticalVelocity);
    const spStats = this._speedStats.push(speed);
    const trStats = this._turnRateStats.push(Math.abs(turnRate));
    const accStats = this._accelerationStats.push(acceleration);

    if (!vvStats || !spStats || !trStats || !accStats) return null;

    const features = {
      // Instantaneous
      verticalVelocity, // m/s, negative = descending
      speed, // m/s
      acceleration, // m/s²
      turnRate, // °/s

      // Windowed statistics
      verticalVelocityMean: vvStats.mean,
      verticalVelocityStd: vvStats.std,
      speedMean: spStats.mean,
      speedStd: spStats.std, // KEY: near-zero for lifts
      turnRateMean: trStats.mean,
      turnRateStd: trStats.std, // KEY: low for lifts
    };

    return {
      ...features,
      // Derived physics (matching Swift computed properties)
      isDescending: features.verticalVelocityMean < -0.5,
      hasConstantSpeed: features.speedStd < 0.5,
      isStraightLine: features.turnRateStd < 5.0,
    };
  }
}


